// components/CommunicationChannel.tsx (excerpt)
"use client";

import Image from "next/image";
import {
    Box, Grid, Link, Paper, Stack, Typography,
} from "@mui/material";
import RslSuperAppBanner from "../RslSuperAppBanner";
import Heading from "../heading";

/* ————— Link click handling —————
   Mirrors the Contact Us page: mailto links open Gmail compose on desktop
   Chrome, the native mail app elsewhere; everything else opens in a new tab. */
function openContactLink(href) {
    if (!href) return;

    if (href.startsWith("mailto:")) {
        const email = href.replace("mailto:", "");

        const isMobile = /Android|iPhone|iPad/i.test(navigator.userAgent);
        const isChromeDesktop =
            navigator.userAgent.includes("Chrome") &&
            !navigator.userAgent.includes("Edg") &&
            !isMobile;

        // Mobile -> native mail app
        if (isMobile) {
            window.location.href = href;
            return;
        }

        // Chrome on Mac/Windows -> Gmail compose (avoids Apple Mail / no handler)
        if (isChromeDesktop) {
            const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(email)}`;
            window.open(gmailUrl, "_blank", "noopener,noreferrer");
            return;
        }

        // Other browsers -> default mail app
        window.location.href = href;
        return;
    }

    // tel: links stay on the same tab so the dialer is triggered
    if (href.startsWith("tel:")) {
        window.location.href = href;
        return;
    }

    window.open(href, "_blank", "noopener,noreferrer");
}

function ContactLink({ href, label }) {
    return (
        <Link
            href={href}
            onClick={(e) => {
                e.preventDefault();
                openContactLink(href);
            }}
            underline="hover"
            sx={{ color: "#0C8B92", fontWeight: 700, fontSize: "14px", cursor: "pointer" }}
        >
            {label}
        </Link>
    );
}

/* ————— Contact Card ————— */
function ContactCard({
    icon, iconSrc, iconAlt, heading, children,
}) {
    return (
        <Paper
            elevation={0}
            sx={{
                px: { xs: 2, md: 2.5 },
                py: { xs: 1.75, md: 2 },
                border: "1px solid #E6EDF3",
                borderRadius: "12px",
                bgcolor: "#fff",
                boxShadow: "0 2px 14px rgba(13,139,146,.06)",
            }}
        >
            <Stack direction="row" alignItems="center" spacing={1.75}>
                {/* Icon badge */}
                <Box
                    sx={{
                        width: 44,
                        height: 44,
                        //         borderRadius: 2,
                        //         display: "grid",
                        //         placeItems: "center",
                        //         border: "1.5px solid #8ED6D1",
                        //         background:
                        //             pattern === "grid"
                        //                 ? `linear-gradient(180deg,#EEF8F7 0%, #F7FBFB 100%), 
                        //    radial-gradient(#dfe9ee 1px, transparent 1px)`
                        //                 : "linear-gradient(180deg,#EEF8F7 0%, #F7FBFB 100%)",
                        //         backgroundSize: pattern === "grid" ? "100% 100%, 10px 10px" : undefined,
                        //         backgroundPosition: "0 0, 0 0",
                        //         overflow: "hidden",
                    }}
                >
                    {iconSrc ? (
                        <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
                            <Image
                                src={iconSrc}
                                alt={iconAlt || heading || "icon"}
                                fill
                                style={{ objectFit: "contain", display: "block" }}
                            />
                        </Box>
                    ) : (
                        icon
                    )}
                </Box>

                {/* Texts */}
                <Box>
                    <Typography
                        sx={{
                            fontSize: 13.5,
                            color: "#68707A",
                            mb: 0.25,
                            whiteSpace: "nowrap",
                        }}
                    >
                        {heading}
                    </Typography>
                    <Typography sx={{ fontWeight: 700, color: "#0C8B92" }}>
                        {children}
                    </Typography>
                </Box>
            </Stack>
        </Paper>
    );
}

/* ————— Section ————— */
export default function CommunicationChannel({ data, onClick }) {
    const { title, subtitle, bottomHeader, cardData, image } = data;
    return (
        <Box sx={{ py: { xs: 5, md: 8 } }}>
            {
                title && subtitle && <Heading title={title} subtitle={subtitle} />
            }


            {
                bottomHeader &&
                <Stack alignItems="center" sx={{ mb: { xs: 3, md: 4 }, px: 2 }}>
                    <Typography
                        sx={{
                            textAlign: "center",
                            fontSize: { xs: 16, md: 18 },
                            letterSpacing: ".01em",
                            fontWeight: 600,
                            maxWidth: 980,
                        }}
                    >
                        {bottomHeader}
                    </Typography>
                </Stack>
            }
            {
                cardData && <Box sx={{ px: { xs: 2, md: 4 } }}>
                    <Grid container spacing={2} sx={{ mt: 2.5, px: 4, pb: { xs: 4, md: 8 }, mx: "auto" }}>
                        {
                            cardData?.map((card, index) => (
                                <Grid item key={index} xs={12} md={4} sx={{ pl: { md: index === 0 && "0 !important" } }}>
                                    <ContactCard
                                        heading={card?.heading}
                                        iconSrc={card?.iconSrc}
                                        iconAlt={card?.iconAlt}
                                    >
                                        {
                                            card?.url && card?.url?.length > 0 ? card?.url?.length === 1 ? (
                                                <ContactLink href={card?.url?.[0]?.href} label={card?.url?.[0]?.label} />
                                            ) : (
                                                <Stack direction="row" spacing={1} flexWrap="wrap">{
                                                    card?.url?.map((link, idx) => (
                                                        link?.href && link?.label ?
                                                            <ContactLink key={idx} href={link?.href} label={link?.label} /> :
                                                            link?.divider &&
                                                            <Typography key={idx} sx={{ color: "#9AA2AA" }}>|</Typography>
                                                    ))}
                                                </Stack>
                                            ) : null
                                        }
                                    </ContactCard>
                                </Grid>
                            ))
                        }
                    </Grid>
                </Box>
            }


            {/* Hero banner */}
            <Box sx={{ px: { xs: 2, md: 4 } }}>
                <RslSuperAppBanner bg={image?.src} title={image?.alt} />
            </Box>
        </Box>
    );
}
